import { useState, useEffect } from "react";
import {
  CalendarDays, Plus, Edit2, Trash2, X, RefreshCw, Check,
  Search, Clock, Users, MapPin, Tag, Eye, TicketIcon, Image,
} from "lucide-react";
import { useAdmin } from "../admin-context";

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  draft:      { cls: "bg-gray-100 text-gray-600",     label: "Draft"      },
  published:  { cls: "bg-blue-100 text-blue-700",     label: "Published"  },
  cancelled:  { cls: "bg-red-100 text-red-700",       label: "Cancelled"  },
  completed:  { cls: "bg-green-100 text-green-700",   label: "Completed"  },
  soldout:    { cls: "bg-amber-100 text-amber-700",   label: "Sold Out"   },
};

const CATEGORIES = [
  "Special Dinner", "Workshop", "Live Music", "Seasonal Menu",
  "Tasting Event", "Private Dining", "Holiday Special", "Other",
];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtCurrency = (v) =>
  v != null ? `₹${Number(v).toLocaleString("en-IN")}` : "—";

// ── Shared UI ─────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || { cls: "bg-gray-100 text-gray-500", label: status };
  return <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
};

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white h-full max-w-lg w-full shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

const FF = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inp  = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent";
const sel  = inp + " bg-white";

// ── Main Page ─────────────────────────────────────────────────────────────────
const EventsAdminPage = () => {
  const { events, loadEvents, createEvent, updateEvent, deleteEvent, showToast } = useAdmin();

  const [view,       setView]       = useState("grid"); // grid | table
  const [search,     setSearch]     = useState("");
  const [catFilter,  setCatFilter]  = useState("all");
  const [statFilter, setStatFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [deleteId,   setDeleteId]   = useState(null);
  const [saving,     setSaving]     = useState(false);

  const emptyForm = {
    title: "", description: "", category: CATEGORIES[0],
    event_date: "", event_time: "19:00", duration_mins: 120,
    location: "", capacity: 50, ticket_price: 0,
    image_url: "", status: "draft", is_featured: false,
    tags: "",
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadEvents && loadEvents(); }, []);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const fBool = (k) => () => setForm(p => ({ ...p, [k]: !p[k] }));

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDrawerOpen(true); };
  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title:         ev.title,
      description:   ev.description  || "",
      category:      ev.category     || CATEGORIES[0],
      event_date:    ev.event_date   || "",
      event_time:    ev.event_time   || "19:00",
      duration_mins: ev.duration_mins || 120,
      location:      ev.location     || "",
      capacity:      ev.capacity     || 50,
      ticket_price:  ev.ticket_price || 0,
      image_url:     ev.image_url    || "",
      status:        ev.status       || "draft",
      is_featured:   !!ev.is_featured,
      tags:          Array.isArray(ev.tags) ? ev.tags.join(", ") : (ev.tags || ""),
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim())  return showToast("Event title is required", "error");
    if (!form.event_date)    return showToast("Event date is required", "error");
    setSaving(true);
    const payload = {
      ...form,
      capacity:      Number(form.capacity),
      ticket_price:  Number(form.ticket_price),
      duration_mins: Number(form.duration_mins),
      tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      license_id: 2,
    };
    try {
      if (editing) {
        await updateEvent(editing.id, payload);
        showToast("Event updated");
      } else {
        await createEvent(payload);
        showToast("Event created");
      }
      setDrawerOpen(false);
    } catch {
      showToast("Failed to save event", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteEvent(id);
      setDeleteId(null);
      showToast("Event deleted");
    } catch {
      showToast("Failed to delete", "error");
    }
  };

  const list = (events || []).filter(ev => {
    const matchCat  = catFilter  === "all" || ev.category === catFilter;
    const matchStat = statFilter === "all" || ev.status   === statFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || (ev.title || "").toLowerCase().includes(q)
      || (ev.location || "").toLowerCase().includes(q);
    return matchCat && matchStat && matchSearch;
  });

  const upcoming = (events || []).filter(ev => new Date(ev.event_date) >= new Date()).length;
  const stats = [
    { label: "Total",     count: (events || []).length,   color: "bg-gray-50 text-gray-700"        },
    { label: "Upcoming",  count: upcoming,                color: "bg-blue-50 text-blue-700"         },
    { label: "Published", count: (events || []).filter(ev => ev.status === "published").length, color: "bg-emerald-50 text-emerald-700" },
    { label: "Draft",     count: (events || []).filter(ev => ev.status === "draft").length,     color: "bg-amber-50 text-amber-700"    },
  ];

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Events</h1>
          <p className="text-sm text-gray-500 mt-0.5">Create and manage special dinners, workshops, and seasonal menus</p>
        </div>
        <div className="flex gap-2">
          <div className="flex p-1 bg-gray-100 rounded-xl">
            {[["grid","Grid"],["table","Table"]].map(([v,l]) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${view === v ? "bg-white shadow text-gray-900" : "text-gray-500"}`}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={() => loadEvents && loadEvents()} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition">
            <RefreshCw size={16} className="text-gray-500" />
          </button>
          <button onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm transition">
            <Plus size={16} /> Create Event
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map(({ label, count, color }) => (
          <div key={label} className={`rounded-2xl p-4 ${color}`}>
            <p className="text-2xl font-extrabold">{count}</p>
            <p className="text-xs font-semibold mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            placeholder="Search events…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
          value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
          value={statFilter} onChange={e => setStatFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_STYLES).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
        </select>
      </div>

      {/* ── Grid View ── */}
      {view === "grid" && (
        list.length === 0
          ? (
            <div className="py-16 text-center text-gray-400">
              <CalendarDays size={32} className="mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No events yet</p>
              <p className="text-sm">Create your first event to get started</p>
            </div>
          )
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {list.map(ev => (
                <div key={ev.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition group">
                  {/* Image */}
                  <div className="relative h-36 bg-gradient-to-br from-orange-100 to-amber-50 flex items-center justify-center overflow-hidden">
                    {ev.image_url
                      ? <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
                      : <CalendarDays size={32} className="text-orange-300" />
                    }
                    {ev.is_featured && (
                      <span className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Featured</span>
                    )}
                    <div className="absolute top-2 right-2"><StatusBadge status={ev.status} /></div>
                  </div>

                  <div className="p-4">
                    <p className="text-xs font-semibold text-orange-500 mb-1">{ev.category}</p>
                    <h3 className="font-bold text-gray-900 text-sm leading-tight mb-2">{ev.title}</h3>

                    <div className="space-y-1 text-xs text-gray-500 mb-3">
                      <p className="flex items-center gap-1.5"><CalendarDays size={11} />{fmtDate(ev.event_date)} at {ev.event_time}</p>
                      {ev.location && <p className="flex items-center gap-1.5"><MapPin size={11} />{ev.location}</p>}
                      <p className="flex items-center gap-1.5">
                        <Users size={11} />{ev.capacity} seats
                        {ev.ticket_price > 0 && <span className="ml-2 font-semibold text-gray-700">{fmtCurrency(ev.ticket_price)}/person</span>}
                      </p>
                    </div>

                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => openEdit(ev)}
                        className="flex-1 py-1.5 text-xs font-semibold bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg transition">
                        Edit
                      </button>
                      <button onClick={() => setDeleteId(ev.id)}
                        className="px-3 py-1.5 text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition">
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
      )}

      {/* ── Table View ── */}
      {view === "table" && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {["Title","Category","Date","Location","Capacity","Price","Status",""].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {list.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No events found</td></tr>
              )}
              {list.map(ev => (
                <tr key={ev.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">{ev.title}</p>
                    {ev.is_featured && <span className="text-[10px] font-bold text-orange-500">Featured</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{ev.category}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-600">{fmtDate(ev.event_date)}<br /><span className="text-xs text-gray-400">{ev.event_time}</span></td>
                  <td className="px-4 py-3 text-gray-500 max-w-[120px] truncate">{ev.location || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{ev.capacity}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{ev.ticket_price > 0 ? fmtCurrency(ev.ticket_price) : "Free"}</td>
                  <td className="px-4 py-3"><StatusBadge status={ev.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(ev)} className="p-1.5 hover:bg-orange-50 rounded-lg transition">
                        <Edit2 size={14} className="text-orange-500" />
                      </button>
                      <button onClick={() => setDeleteId(ev.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition">
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add/Edit Drawer ── */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Edit Event" : "Create Event"}>
        <div className="space-y-4">
          <FF label="Event Title" required>
            <input className={inp} value={form.title} onChange={f("title")} placeholder="e.g. Festive Tasting Dinner" />
          </FF>
          <FF label="Description">
            <textarea rows={3} className={inp} value={form.description} onChange={f("description")} placeholder="Describe the event…" />
          </FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Category">
              <select className={sel} value={form.category} onChange={f("category")}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </FF>
            <FF label="Status">
              <select className={sel} value={form.status} onChange={f("status")}>
                {Object.entries(STATUS_STYLES).map(([v, { label }]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </FF>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Date" required>
              <input type="date" className={inp} value={form.event_date} onChange={f("event_date")} />
            </FF>
            <FF label="Time">
              <input type="time" className={inp} value={form.event_time} onChange={f("event_time")} />
            </FF>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Duration (mins)">
              <input type="number" min={15} step={15} className={inp} value={form.duration_mins}
                onChange={e => setForm(p => ({...p, duration_mins: Number(e.target.value)}))} />
            </FF>
            <FF label="Capacity (seats)">
              <input type="number" min={1} className={inp} value={form.capacity}
                onChange={e => setForm(p => ({...p, capacity: Number(e.target.value)}))} />
            </FF>
          </div>
          <FF label="Location / Venue">
            <input className={inp} value={form.location} onChange={f("location")} placeholder="e.g. Main Hall, Rooftop, Branch Name" />
          </FF>
          <FF label="Ticket Price (₹, 0 = Free)">
            <input type="number" min={0} className={inp} value={form.ticket_price}
              onChange={e => setForm(p => ({...p, ticket_price: Number(e.target.value)}))} />
          </FF>
          <FF label="Cover Image URL">
            <input className={inp} value={form.image_url} onChange={f("image_url")} placeholder="https://…" />
          </FF>
          <FF label="Tags (comma-separated)">
            <input className={inp} value={form.tags} onChange={f("tags")} placeholder="romantic, wine-pairing, outdoor" />
          </FF>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.is_featured} onChange={fBool("is_featured")}
              className="w-4 h-4 rounded accent-orange-500" />
            <span className="text-sm font-semibold text-gray-700">Feature this event on homepage</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setDrawerOpen(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {editing ? "Save Changes" : "Create Event"}
            </button>
          </div>
        </div>
      </Drawer>

      {/* ── Delete Confirm ── */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-80">
            <h3 className="font-bold text-gray-900 mb-2">Delete Event?</h3>
            <p className="text-sm text-gray-500 mb-5">This will permanently remove the event.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsAdminPage;
